import React, { useState } from 'react';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@vaiu/ui/collapsible';
import { Coins, ChevronDown, Gift, Share2, Sparkles } from 'lucide-react';
import { i18n } from '@/utils/i18n';
import { usePaymentsParadigmGuideVisibility } from './usePaymentsParadigmGuideVisibility';

export type PaymentsParadigmGuideVariant = 'dashboard';

export interface PaymentsParadigmGuideProps {
  variant?: PaymentsParadigmGuideVariant;
  /** When true, never render (e.g. embed shells). */
  forceHidden?: boolean;
  onSenderPath?: () => void;
  onReceiverPath?: () => void;
}

const PaymentsParadigmGuide: React.FC<PaymentsParadigmGuideProps> = ({
  variant = 'dashboard',
  forceHidden = false,
  onSenderPath,
  onReceiverPath,
}) => {
  const { visible, dismiss } = usePaymentsParadigmGuideVisibility();
  const [open, setOpen] = useState(true);

  if (forceHidden || !visible || variant !== 'dashboard') {
    return null;
  }

  return (
    <Card className="border-dashed shadow-sm bg-muted/30">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 sm:px-6">
            <CollapsibleTrigger
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 text-left text-base font-semibold outline-none hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Sparkles className="h-5 w-5 shrink-0 text-primary" aria-hidden />
              <span className="truncate">{i18n.__('core.paymentsGuide.title')}</span>
              <ChevronDown
                className={`ml-auto h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </CollapsibleTrigger>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 text-muted-foreground"
              onClick={(e) => {
                e.stopPropagation();
                dismiss();
              }}
            >
              {i18n.__('core.paymentsGuide.dismiss')}
            </Button>
          </div>
          <CollapsibleContent>
            <div className="space-y-4 px-4 py-4 sm:px-6">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {i18n.__('core.paymentsGuide.summary')}
              </p>
              <ul className="grid gap-3 text-sm sm:grid-cols-1">
                <li className="flex gap-3 rounded-md bg-background/60 p-3">
                  <Coins className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{i18n.__('core.paymentsGuide.step1')}</span>
                </li>
                <li className="flex gap-3 rounded-md bg-background/60 p-3">
                  <Share2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{i18n.__('core.paymentsGuide.step2')}</span>
                </li>
                <li className="flex gap-3 rounded-md bg-background/60 p-3">
                  <Gift className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{i18n.__('core.paymentsGuide.step3')}</span>
                </li>
              </ul>
              <p className="text-xs text-muted-foreground border-l-2 border-amber-500/60 pl-3">
                {i18n.__('core.paymentsGuide.trustLine')}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <Button type="button" variant="default" size="sm" onClick={onSenderPath}>
                  {i18n.__('core.paymentsGuide.senderCta')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onReceiverPath}>
                  {i18n.__('core.paymentsGuide.receiverCta')}
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Collapsible>
    </Card>
  );
};

export default PaymentsParadigmGuide;
